import { html } from "lit";
import { formatHtml } from "@trumpf/ux.common-storybook";
import { getPlaceholderUrl } from "../../../src/utils/getPlaceholderUrl";
import page, {
    pageWithMarginalColumn,
    pageWithMarginalColumnFullWidth,
    pageWithSidebar,
} from "./shared/page";

export const baiduMapsClientKey = "IypnxYxjL2lExCpqswNhUmvFBpfFN5mb";

export { formatHtml, getPlaceholderUrl };

export const noLegacyStylesDecorator = (Story) => {
    return html`
        <script>
            document.addEventListener("DOMContentLoaded", function () {
                document.querySelector("#legacy-stylesheet")?.remove();
            });
        </script>
        ${Story()}
    `;
};

export const renderStandardPictureElement = ({
    width = 400,
    height = 400,
    isInSlider = false,
    imgSrc = undefined,
    isSourceCode = false,
}) => {
    if (isSourceCode) {
        return html`
            <!-- STANDARD PICTURE ELEMENT -->
            <picture>
                <source src="#" media="(max-width: 567px)" />
                <source src="#" />
                <img src="#" alt="#" />
            </picture>
        `;
    }
    return !isInSlider
        ? html`
              <picture>
                  <source
                      media="(max-width: 567px)"
                      data-srcset="${imgSrc
                          ? imgSrc
                          : getPlaceholderUrl(width, height)}"
                  />
                  <source
                      media="(max-width: 1024px)"
                      data-srcset="${imgSrc
                          ? imgSrc
                          : getPlaceholderUrl(width, height)}"
                  />
                  <source
                      data-srcset="${imgSrc
                          ? imgSrc
                          : getPlaceholderUrl(width, height)}"
                  />
                  <img
                      class="lazyload"
                      alt="17:8 standard - viewport test images /w Retina support"
                      src="${getPlaceholderUrl(width, height)}"
                      data-src="${imgSrc
                          ? imgSrc
                          : getPlaceholderUrl(width, height)}"
                  />
              </picture>
          `
        : html`
              <picture>
                  <source
                      media="(max-width: 567px)"
                      data-srcset="${imgSrc
                          ? imgSrc
                          : getPlaceholderUrl(width, height)}"
                      class="ux-slider-lazy-img"
                  />
                  <source
                      media="(max-width: 1024px)"
                      data-srcset="${imgSrc
                          ? imgSrc
                          : getPlaceholderUrl(width, height)}"
                      class="ux-slider-lazy-img"
                  />
                  <source
                      data-srcset="${imgSrc
                          ? imgSrc
                          : getPlaceholderUrl(width, height)}"
                      class="ux-slider-lazy-img"
                  />
                  <img
                      class="ux-slider-lazy-img"
                      alt="17:8 standard - viewport test images /w Retina support"
                      src="${getPlaceholderUrl(width, height)}"
                      data-src="${imgSrc
                          ? imgSrc
                          : getPlaceholderUrl(width, height)}"
                  />
              </picture>
          `;
};

export const widthDecorator = (Story, width) => {
    return html`
        <div style="width: ${width}; max-width: 100%;">${Story()}</div>
    `;
};

const loremIpsumText =
    "Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est.";

export function getDummyText(len = 100) {
    return (
        Array(Math.ceil(len / loremIpsumText.length))
            .fill(loremIpsumText)
            .join(" ")
            .split(" ")
            .reduce((accumulator, currentValue) => {
                if (!accumulator || !accumulator.length) {
                    return currentValue;
                }
                if (accumulator.length >= len) {
                    return accumulator;
                }
                return accumulator + " " + currentValue;
            }) + "."
    );
}

export function getDummyHtml() {
    return html`
        <p>${getDummyText(80)} <b>Lorem ipsum bold</b> ${getDummyText(200)}</p>
        <p>
            ${getDummyText(110)} <b>Lorem ipsum bold long</b> ${getDummyText(
                70
            )}
        </p>
    `;
}

/**
 * The div with id "scripts-root" causes the screenshot tests to fail because
 * it datects DOM differences between edge and chrome.
 * This function removes that div.
 * Normally only storybook solar-related scripts are in that div.
 * Only to be used on template pages that need to be screenshot tested.
 */
export const removeStorybookScripts = (htmlTemplate) => {
    return html`
        <script>
            document.querySelector("#scripts-root").remove();
        </script>
        ${htmlTemplate}
    `;
};

export const uxSelect = ({
    required = true,
    label = "Label",
    name = "ux-select-name",
    invalidMsg = "This field is invalid",
    requiredMrg = "This field is required",
}) => {
    return html`
        <ux-select
            required="${required}"
            label="${label}"
            name="${name}"
            data-validation-required--msg="${requiredMrg}"
            data-validation-invalid--msg="${invalidMsg}"
            full-width
        >
            <option value="DE" selected>Deutschland</option>
            <option value="AT">Österreich</option>
            <option value="CH">Schweiz</option>
        </ux-select>
    `;
};

export const uxInput = () => {
    return html`
        <ux-input-field
            label="Titel"
            type="input"
            name="input-title"
            data-validation-required--msg="Dies ist ein Pflichtfeld."
            data-validation-invalid--msg="Die Eingabe ist ungültig."
        >
        </ux-input-field>
    `;
};

export const uxPhoneInput = () => {
    return html`
        <ux-phone-input
            required
            language="de"
            label="Telefonnummer "
            country="DE"
            name="input-phone"
        ></ux-phone-input>
    `;
};

export const uxFormGroup = () => {
    return html`
        <ux-form-group label="Notification permissions" name="form-group-radio">
            <ux-radio-button
                name="radio-ident-number"
                label="Kundennummer"
                value="company-number"
            >
                <ux-icon name="info" id="tooltip-company-number" />
            </ux-radio-button>

            <ux-tooltip
                tooltip-target="ux-icon#tooltip-company-number"
                align="right"
            >
                <p class="ux-text-normal">
                    Die Kundennummer finden Sie auf dem Verkaufsbeleg oder ihrem
                    Angebot.<br />
                    Es handelt sich dabei um eine 6-stellige Nummer (######)
                </p>
            </ux-tooltip>

            <ux-radio-button
                name="radio-ident-number"
                label="Machinenummer"
                value="machine-number"
            >
                <ux-icon name="info" id="tooltip-machine-number" />
            </ux-radio-button>
            <ux-tooltip
                tooltip-target="ux-icon#tooltip-machine-number"
                align="right"
            >
                <p class="ux-text-normal">
                    Die Seriennummer finden Sie auf dem Namensschild ihrer
                    Maschine.<br />
                    Seriennummern können sein:
                </p>
                <ul class="ux-list">
                    <li>10 Ziffern (X####X####)</li>
                    <li>6 Ziffern (######)</li>
                </ul>
            </ux-tooltip>
        </ux-form-group>
    `;
};

export const uxCheckbox = () => {
    return html`
        <ux-checkbox value="terms" name="terms" required>
            Hiermit akzeptiere ich die
            <a href="#">Nutzungsbedingungen</a>.
        </ux-checkbox>
    `;
};

/**
 * add translations for ux-i18n to the head of the document
 * @param {string} options.namespace unique identifier for the namespace of the translations
 * @param {string} options.language identifier for the language of the translations eg "de-DE"
 * @param {object} options.translations JSON object with the translations
 */

// Implementation of the function
export const addI18nTranslations = ({ namespace, language, translations }) => {
    const script = document.createElement("script");
    script.setAttribute("type", "application/json");
    script.setAttribute("data-ux-i18n", "");
    script.setAttribute("data-ux-i18n-namespace", namespace);
    script.setAttribute("data-ux-i18n-language", language);
    script.innerHTML = JSON.stringify(translations);
    document.head.appendChild(script);
};

export const transformSourceCode = (code: string): string => {
    const startComment = "<!-- BEGIN Story -->";
    const startIndex = code.indexOf(startComment);
    const endIndex = code.indexOf("<!-- END Story -->");
    if (startIndex !== -1 && endIndex > startIndex) {
        return formatHtml(
            code.slice(startIndex + startComment.length, endIndex)
        );
    }
    return formatHtml(code);
};

// Used in SectionsSlider
export function slideContent(titleText) {
    return html`
        <ux-iws-link-wrapper class="ux-teaser-tile ux-teaser-tile--vertical">
            <div class="ux-teaser-tile__overlay"></div>
            ${renderStandardPictureElement({
                width: 341,
                height: 455,
                isInSlider: true,
            })}
            <a class="ux-teaser-tile__content" href="#">
                <h3
                    class="ux-teaser-tile__title ux-text-headline ux-text-headline--small ux-u-text-bold"
                >
                    ${titleText}
                </h3>
                <span class="ux-teaser-tile__cta"
                    >Mehr erfahren <ux-icon name="arrow-small-right"></ux-icon
                ></span>
            </a>
        </ux-iws-link-wrapper>
    `;
}

export const renderPageTemplate = (template) => {
    if (window.location.href.includes("--docs")) {
        return template();
    } else {
        return page(template());
    }
};

export const renderPageWithMarginalColumnTemplate = (template) => {
    if (window.location.href.includes("--docs")) {
        return template();
    } else {
        return pageWithMarginalColumn(template());
    }
};

export const renderPageWithMarginalColumnFullWidthTemplate = (template) => {
    if (window.location.href.includes("--docs")) {
        return template();
    } else {
        return pageWithMarginalColumnFullWidth(template());
    }
};

export const renderPageWithSidebarTemplate = (template) => {
    if (window.location.href.includes("--docs")) {
        return template();
    } else {
        return pageWithSidebar(template());
    }
};
